/**
 * Copyright (C) 2021 - 2025 Philips Domestic Appliances Holding B.V.
 * All rights are reserved.
 */

import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { JsonApiDatastoreConfig } from '../../../assets/vendors/angular2-jsonapi/decorators/json-api-datastore-config.decorator';
import { DatastoreConfig } from '../../../assets/vendors/angular2-jsonapi/interfaces/datastore-config.interface';
import { JsonApiQueryData } from '../../../assets/vendors/angular2-jsonapi/models/json-api-query-data';
import { JsonApiModel } from '../../../assets/vendors/angular2-jsonapi/models/json-api.model';
import {
	JsonApiDatastore,
	ModelType,
} from '../../../assets/vendors/angular2-jsonapi/services/json-api-datastore.service';
import { APP_CONFIG, IAppConfig } from '../../app.config';
import { HttpService } from '../../services/http/http.service';
import { getDirtyAttributes } from '../../utils/overrides/json-api-datastore/get-dirty-attributes';
import { toQueryString } from '../../utils/overrides/json-api-datastore/to-query-string';
import { ErrorService } from '../error/error.service';

export type CustomRequestOptions =
	| {
			body?: object;
			observe?: string;
			responseType?: string;
			fullResponse?: boolean; // if true, HttpResponse will be returned instead of parsed JSON
			headers?: object;
			[K: string]: any;
	  }
	| any;

@Injectable()
@JsonApiDatastoreConfig({
	overrides: {
		toQueryString,
		getDirtyAttributes,
	},
})
export class Datastore extends JsonApiDatastore {
	protected config: DatastoreConfig = {
		baseUrl: this.appConfig.api.host,
		apiVersion: this.appConfig.api.endpoint,
	};

	constructor(
		private readonly httpService: HttpService,
		@Inject(APP_CONFIG) private readonly appConfig: IAppConfig,
		private errorService: ErrorService,
		private readonly injector: Injector,
	) {
		super(httpService);
	}

	public updateHasManyRelationship(
		endpointUrl: string,
		relationshipModels: Array<any>,
		modelType: string,
	): Observable<any> {
		const data = relationshipModels.map((model) => ({
			id: model.id,
			type: modelType,
		}));

		const options: CustomRequestOptions = {
			body: {
				data,
			},
		};

		return this.makeHttpRequest('PATCH', endpointUrl, options);
	}

	protected handleError(errorResponse: any): Observable<any> {
		// HACK: for some reason Datastore constructor is called twice on app initialization
		// and one of those two calls don't have appropriate parameters
		if (!this.errorService) {
			this.errorService = this.injector.get(ErrorService);
		}

		return this.errorService.globalErrorHandler(errorResponse);
	}

	public makeHttpRequest<T extends JsonApiModel>(
		method = 'GET',
		endpointUrl: string,
		options: CustomRequestOptions = {},
		model?: T,
		modelType?: ModelType<T>,
		apiVersion?: string,
	): Observable<JsonApiQueryData<T> | HttpResponse<T> | T> {
		const url: string = this.generateUrl(endpointUrl, apiVersion);

		let request$;

		switch (method) {
			case 'GET':
				request$ = this.makeHttpGetRequest<T>(url, options);
				break;
			case 'PUT':
				request$ = this.makeHttpPutRequest<T>(url, options.body, options);
				break;
			case 'PATCH':
				request$ = this.makeHttpPatchRequest<T>(url, options.body, options);
				break;
			case 'POST':
				request$ = this.makeHttpPostRequest<T>(url, options.body, options);
				break;
			case 'DELETE':
				request$ = this.makeHttpDeleteRequest<T>(url, options);
				break;
			default:
				console.error(
					`Not supported method ${method} for a custom call. Check makeHttpRequest for more information.`,
				);
				return of({}) as Observable<T>;
		}

		return request$.pipe(
			map((response: HttpResponse<T>) => {
				if (model && modelType) {
					const extractedModel = this['extractRecordData'](response, modelType, model);
					return extractedModel;
				} else {
					const response1 = options.fullResponse ? response : response.body;
					return response1;
				}
			}),
			catchError(this.handleError.bind(this)),
		);
	}

	private makeHttpGetRequest<T>(
		url: string,
		options: CustomRequestOptions = {},
	): Observable<HttpResponse<T>> {
		const opts = Object.assign(options, {
			observe: 'response',
		});

		return this.httpService.get<T>(url, opts) as Observable<HttpResponse<T>>;
	}

	private makeHttpDeleteRequest<T>(
		url: string,
		options: CustomRequestOptions = {},
	): Observable<HttpResponse<T>> {
		const opts = Object.assign(options, {
			observe: 'response',
		});

		return this.httpService.delete<T>(url, opts) as Observable<HttpResponse<T>>;
	}

	private makeHttpPutRequest<T>(
		url: string,
		body: object,
		options: CustomRequestOptions = {},
	): Observable<HttpResponse<T>> {
		const opts = Object.assign(options, {
			observe: 'response',
		});

		return this.httpService.put<T>(url, body, opts) as Observable<HttpResponse<T>>;
	}

	private makeHttpPatchRequest<T>(
		url: string,
		body: object,
		options: CustomRequestOptions = {},
	): Observable<HttpResponse<T>> {
		const opts = Object.assign(options, {
			observe: 'response',
		});

		return this.httpService.patch<T>(url, body, opts) as Observable<HttpResponse<T>>;
	}

	private makeHttpPostRequest<T>(
		url: string,
		body: object,
		options: CustomRequestOptions = {},
	): Observable<HttpResponse<T>> {
		const opts = Object.assign(options, {
			observe: 'response',
		});

		return this.httpService.post<T>(url, body, opts) as Observable<HttpResponse<T>>;
	}

	public customEndpointQueryData<T extends JsonApiModel>(
		endpointUrl: string,
		modelType: ModelType<T>,
		withMeta?: boolean,
		options: CustomRequestOptions = {},
	): Observable<JsonApiQueryData<T> | Array<T>> {
		const queryData$: Subject<JsonApiQueryData<T> | Array<T>> = new Subject();

		const requestOptions: CustomRequestOptions = Object.assign(options, { fullResponse: true });

		this.makeHttpRequest('GET', endpointUrl, requestOptions)
			.pipe(catchError((error: any) => throwError(error)))
			.subscribe((response: HttpResponse<T>) => {
				const extractedModels = this['extractQueryData'](response, modelType, withMeta);
				queryData$.next(extractedModels);
			});

		return queryData$;
	}

	public generateUrl(
		endpointUrl: string,
		apiVersion: string = this.appConfig.api.endpoint,
		baseUrl: string = this.appConfig.api.host,
	): string {
		return [baseUrl, apiVersion, endpointUrl].join('/');
	}
}
